import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, addDoc, collection } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReactMarkdown from 'react-markdown';

function HelpContextDetail() {
  const { contextId } = useParams();
  const navigate = useNavigate();
  const isNewContext = contextId === 'new';
  
  const [loading, setLoading] = useState(!isNewContext);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [helpContext, setHelpContext] = useState({
    contextId: '',
    data: ''
  });

  useEffect(() => {
    const fetchHelpContext = async () => {
      if (isNewContext) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const contextDoc = await getDoc(doc(db, 'help-context', contextId));
        
        if (contextDoc.exists()) {
          const data = contextDoc.data();
          setHelpContext({
            contextId: data.contextId || '',
            data: data.data || ''
          });
        } else {
          setError('Help context not found');
        }
      } catch (err) {
        console.error('Error fetching help context:', err);
        setError('Failed to load help context');
      } finally {
        setLoading(false);
      }
    };

    fetchHelpContext();
  }, [contextId, isNewContext]);

  const handleChange = (field, value) => {
    setHelpContext(prev => ({
      ...prev,
      [field]: value
    }));
    setError(null);
    setSuccess(false);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validation
      if (!helpContext.contextId.trim()) {
        setError('Context ID is required');
        setLoading(false);
        return;
      }

      if (!helpContext.data.trim()) {
        setError('Data is required');
        setLoading(false);
        return;
      }

      const contextData = {
        contextId: helpContext.contextId,
        data: helpContext.data
      };

      if (isNewContext) {
        const docRef = await addDoc(collection(db, 'help-context'), contextData);
        setSuccess(true);
        setTimeout(() => {
          navigate(`/help-context/${docRef.id}`);
        }, 1000);
      } else {
        await updateDoc(doc(db, 'helpContext', contextId), contextData);
        setSuccess(true);
      }
    } catch (err) {
      console.error('Error saving help context:', err);
      setError('Failed to save help context');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !isNewContext) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/help-context')}
        sx={{ mb: 2 }}
      >
        Back to Help Context
      </Button>

      <Typography variant="h4" gutterBottom>
        {isNewContext ? 'New Help Context' : 'Edit Help Context'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Help context saved successfully!
        </Alert>
      )}

      <Paper sx={{ p: 3, mt: 2, maxWidth: 900, mx: 'auto' }}>
        <Box sx={{ mb: 3 }}>
          <TextField
            label="Context ID"
            value={helpContext.contextId}
            onChange={(e) => handleChange('contextId', e.target.value)}
            required
            helperText="A unique identifier for this help context (e.g., 'home', 'lakes', 'fishing-tips')"
            sx={{ mb: 3, width: 600 }}
            disabled={!isNewContext}
          />
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Box sx={{ mb: 3 }}>
          <TextField
            label="Data (Markdown)"
            value={helpContext.data}
            onChange={(e) => handleChange('data', e.target.value)}
            multiline
            rows={20}
            required
            helperText="Use Markdown formatting - preview below"
            sx={{
              width: '100%',
              '& .MuiInputBase-input': {
                fontFamily: 'monospace',
              }
            }}
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Preview
          </Typography>
          <Paper 
            variant="outlined" 
            sx={{ 
              p: 2, 
              minHeight: 200,
              backgroundColor: '#f5f5f5',
              overflow: 'auto',
              wordWrap: 'break-word',
              '& h1, & h2, & h3, & h4, & h5, & h6': {
                marginTop: 2,
                marginBottom: 1,
                wordWrap: 'break-word'
              },
              '& p': {
                marginBottom: 1,
                wordWrap: 'break-word'
              },
              '& ul, & ol': {
                marginLeft: 2
              },
              '& code': {
                backgroundColor: '#e0e0e0',
                padding: '2px 4px',
                borderRadius: 1,
                wordWrap: 'break-word',
                overflowWrap: 'break-word'
              },
              '& pre': {
                backgroundColor: '#e0e0e0',
                padding: 2,
                borderRadius: 1,
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word'
              },
              '& img': {
                maxWidth: '100%',
                height: 'auto'
              },
              '& a': {
                wordWrap: 'break-word',
                overflowWrap: 'break-word'
              }
            }}
          >
            {helpContext.data ? (
              <ReactMarkdown>{helpContext.data}</ReactMarkdown>
            ) : (
              <Typography color="text.secondary" fontStyle="italic">
                Preview will appear here...
              </Typography>
            )}
          </Paper>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/help-context')}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

export default HelpContextDetail;
